import React, { useRef, useState, useEffect } from 'react';
import { Canvas } from '@react-three/fiber';
import { Html, PerspectiveCamera, Sphere } from '@react-three/drei'
import * as THREE from 'three';
import * as tf from '@tensorflow/tfjs';
import { Grid, Box, Typography } from '@mui/material';

import itertools from './itertools';
import chebyshevLP from './LP';
import TensorViz from './TensorViz';
import Polytope from './Polytope';
import SignVector from './SignVector';


class Colors {
    constructor () {
        this.red = '#cd533b';
        this.green = '#30a147';
        this.blue = '#0075a2';
        this.yellow = '#faa916';
        this.purple = '#6a5b6e';
        this.teal = '#7dcfb6';
        this.darkblue = '#555599';
        this.darkred = '#995555';
        // this.darkgreen = 0x559955;
        // this.white = 0xf1f6f6;
        // this.black = 0x1a1a1a;
        this.colors = [
                       this.blue,
                       this.red,
                       this.yellow,
                       this.purple,
                       this.green,
                       this.teal,
                       this.darkblue,
                       this.darkred,
        ];
    }
}


const TensorWidget = (props) => {
  const widgetRef = useRef();
  const Nrows = props.polytope.arraySync().length;
  const N = props.N;
  const [feasible, setFeasible] = useState([]);
  const [regions, setRegions] = useState([]);
  // const init = tf.randomNormal([4, 2]);
  const init = tf.randomUniform([N, 2], -1, 1);
  const [tensor, setTensor] = useState(init);
  const colors = new Colors().colors;

  const setRow = (i, row) => {
    const prev = tensor.arraySync();
    prev[i] = [row.x, row.y];
    setTensor(new tf.tensor2d(prev));

    // Compute feasibility signvectors
    // each time weight vectors change
    computeFeasible();
  }

  const computeFeasible = () => {
    let key = 0;
    let W = tensor.arraySync();
    let reg = [];
    let feas = [];
    for (let each of itertools.subsetIdxs(N)) {
      let signvec = Array(N).fill('-');
      for (let e of each) {
        signvec[e] = '+';
      }
      const problem = chebyshevLP(each, W, -1, 1);
      const sol = window.highs.solve(problem);
      if ( sol['Status'] == 'Optimal' ) {
        let cs = sol['Columns'];
        let p = new THREE.Vector3(cs['x1']['Primal'], cs['x2']['Primal'], 0).normalize().multiplyScalar(.75);
        let sp = {'scale': sol.ObjectiveValue,
                  'position': p,
                  'signvec': signvec
                  };
        reg.push(sp);
        key++;
        feas.push(true);
      }
      else {
        feas.push(false);
      }
    }
    setFeasible(feas);
    setRegions(reg);
  }

  useEffect(() => {
    computeFeasible();
  }, [props.polytope])

  useEffect(() => {
    const init = tf.randomUniform([N, 2], -1, 1);
    setTensor(init);
    computeFeasible();
  }, [N])

  return (
      <Grid container>
        <Grid item xs={12} md={6} className="grid-item">
          <Box className="halfscreen left">
            <Canvas>
              <Html fullscreen>
                <Typography className="title left"><h2>Hyperplane View</h2></Typography>
              </Html>
              <PerspectiveCamera makeDefault position={[0, 0, 2]} />
              <ambientLight />
              <pointLight position={[30, 20, 10]} />
              <group ref={widgetRef}>
                <TensorViz tensor={tensor} colors={colors} setRow={setRow} />
                {/* {regions.map((row, rowIndex) => ( */}
                {/*   <Sphere args={[.018, 16, 16]} key={rowIndex} position={new THREE.Vector3(row.position.x, row.position.y, 0)}> */}
                {/*     <meshStandardMaterial transparent color="#333"  opacity={.95} /> */}
                {/*   </Sphere> */}
                {/* ))} */}
                {regions.map((row, rowIndex) => (
                  <SignVector key={rowIndex} text={row.signvec} colors={colors} position={new THREE.Vector3(row.position.x, row.position.y, 0)} />
                ))}
              </group>
            </Canvas>
          </Box>
        </Grid>
        <Grid item xs={12} md={6} className="grid-item">
          <Box className="halfscreen right">
            <Canvas>
              <Html fullscreen>
                <Typography className="title"><h2>Zonotope View</h2></Typography>
              </Html>
              <PerspectiveCamera makeDefault position={[0, 0, 2]} />
              <ambientLight />
              <pointLight position={[30, 20, 10]} />
              <Polytope points={props.polytope} proj={tensor} feasible={feasible} colors={colors} />
            </Canvas>
          </Box>
        </Grid>
      </Grid>
  );
}

export default TensorWidget;
